import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {HTMLInputElement[]} */
  #searchInputs = [];

  /** @type {string} */
  #sharedSearchValue = '';

  connectedCallback() {
    super.connectedCallback();
    this.#initializeSearchInputs();
    this.refs.menuDrawer.addEventListener('input', this.#onSearchValueChange, true);
    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.bannerElement = document.querySelector('.shopify-section-group-pre-header');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.menuDrawer?.removeEventListener('input', this.#onSearchValueChange, true);
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Keep all drawer search fields synchronized while typing.
   * @param {Event} event
   */
  #onSearchValueChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (!event.target.classList.contains('search-input')) return;

    this.#sharedSearchValue = event.target.value;
    this.#searchInputs.forEach((input) => {
      if (input === event.target) return;
      if (input.value === this.#sharedSearchValue) return;
      input.value = this.#sharedSearchValue;
    });
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    this.bannerElement?.classList.add('menu-drawer--has-opened');
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    if (target) {
      this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');

      // A level-3 details sits inside a .menu-drawer__submenu--level2 ancestor.
      // We specifically look for that class so we don't trigger on level-2 opens.
      const parentLevel2Submenu = details.closest('.menu-drawer__submenu--level2');
      if (parentLevel2Submenu) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-grandchild-opened');
        parentLevel2Submenu.classList.add('menu-drawer--has-submenu-opened');
      }
    }

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      this.#applySharedSearchValue(details);

      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.bannerElement?.classList.remove('menu-drawer--has-opened');
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');
    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    // Only treat this as a level-3 close if the details being closed
    // lives inside a level-2 submenu specifically.
    const parentLevel2Submenu = details.closest('.menu-drawer__submenu--level2');

    if (parentLevel2Submenu) {
      // Going from level 3 → level 2: remove only the grandchild classes.
      this.refs.menuDrawer.classList.remove('menu-drawer--has-grandchild-opened');
      parentLevel2Submenu.classList.remove('menu-drawer--has-submenu-opened');
    } else {
      // Going from level 2 → level 1, or closing the whole drawer.
      this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
      this.refs.menuDrawer.classList.remove('menu-drawer--has-grandchild-opened');
      this?.classList.remove('menu-drawer--has-opened');
    }

    // Wait for the .menu-drawer element's transition, not the entire details subtree.
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox.
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          /** @type {NodeListOf<HTMLDetailsElement>} */
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          this.#applySharedSearchValue(this.refs.details);
          trapFocus(this.refs.details);
        }
      },
      { subtree: false },
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', (event) => {
        removeWillChangeOnAnimationEnd(/** @type {AnimationEvent} */(event));
      });
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }

  #initializeSearchInputs() {
    this.#searchInputs = Array.from(this.querySelectorAll('.menu-drawer .search-input'));

    const firstNonEmptyInput = this.#searchInputs.find((input) => input.value.trim() !== '');
    if (firstNonEmptyInput) {
      this.#sharedSearchValue = firstNonEmptyInput.value;
      this.#searchInputs.forEach((input) => {
        input.value = this.#sharedSearchValue;
      });
    }
  }

  /**
   * Ensure the active drawer level receives the latest typed search value before focus is trapped.
   * @param {HTMLDetailsElement} details
   */
  #applySharedSearchValue(details) {
    const contextualInput = details.querySelector('.search-input');

    if (contextualInput instanceof HTMLInputElement && contextualInput.value !== this.#sharedSearchValue) {
      contextualInput.value = this.#sharedSearchValue;
    }
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}